/**
 * PlayerSidebar.tsx
 * =============================================================================
 * Sidebar component displaying audio modes and timer controls.
 * Pure presentational component - receives props and emits events.
 *
 * @component
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { AudioMode, TimerProfileId } from '../../core/types';
import { MODE_CONFIGS } from '../../config/audioPresets';
import { Brain, Moon, Heart, FlaskConical } from 'lucide-react';
import { TimerControls } from './TimerControls';

// =============================================================================
// TYPES
// =============================================================================

interface PlayerSidebarProps {
  modes: AudioMode[];
  currentModeId: string;
  onSelectMode: (id: string) => void;

  // Timer v3.0
  currentTimerProfile: TimerProfileId;
  onSelectTimerProfile: (profileId: TimerProfileId) => void;
}

// =============================================================================
// ICON MAPPING
// =============================================================================

const MODE_ICONS: Record<AudioMode, React.ComponentType<{ className?: string }>> = {
  focus: Brain,
  sleep: Moon,
  anxiety: Heart,
};

// =============================================================================
// COMPONENT
// =============================================================================

export const PlayerSidebar: React.FC<PlayerSidebarProps> = ({
  modes,
  currentModeId,
  onSelectMode,
  currentTimerProfile,
  onSelectTimerProfile,
}) => {
  return (
    <aside className="w-full md:w-80 bg-neutral-900/80 backdrop-blur-sm md:border-r border-b md:border-b-0 border-white/5 flex flex-col md:h-full">
      {/* Header - Hidden on mobile */}
      <div className="hidden md:block p-6 border-b border-white/5 flex-shrink-0">
        <h2 className="text-lg font-semibold text-white/90 tracking-wide">
          Modos de Audio
        </h2>
        <p className="text-sm text-white/50 mt-1">
          Selecciona tu estado mental
        </p>
      </div>

      {/* ================================================================ */}
      {/* MOBILE: Horizontal Carousel with External Description */}
      {/* ================================================================ */}
      <div className="md:hidden pt-6">
        {/* Top Row: Mode Selectors - Active always in center */}
        <div className="flex flex-row justify-center items-end gap-3 px-4 py-2">
          {(() => {
            // Reorder modes: [inactive, ACTIVE, inactive]
            const activeMode = modes.find((id) => id === currentModeId);
            const inactiveModes = modes.filter((id) => id !== currentModeId);
            const orderedModes = [inactiveModes[0], activeMode!, inactiveModes[1]];

            return orderedModes.map((modeId) => {
              const config = MODE_CONFIGS[modeId];
              const isActive = currentModeId === modeId;
              const IconComponent = MODE_ICONS[modeId];

              return (
                <button
                  key={modeId}
                  onClick={() => onSelectMode(modeId)}
                  className={`
                    ${isActive ? 'w-24' : 'w-16'}
                    ${isActive ? 'py-3 px-2' : 'py-2 px-2'}
                    rounded-xl transition-all duration-300
                    flex flex-col items-center justify-center gap-1.5
                    ${
                      isActive
                        ? 'bg-white/10 border-2 shadow-lg'
                        : 'bg-white/5 border border-white/10 opacity-50 hover:opacity-80'
                    }
                  `}
                  style={{
                    borderColor: isActive ? config.color : 'transparent',
                    boxShadow: isActive ? `0 0 20px ${config.color}40` : 'none',
                  }}
                  aria-label={`Seleccionar modo ${config.name}`}
                  aria-pressed={isActive}
                >
                  {/* Icon */}
                  <div
                    className={`
                      ${isActive ? 'p-2' : 'p-2'}
                      rounded-lg transition-colors
                    `}
                    style={{
                      backgroundColor: isActive ? `${config.color}20` : undefined,
                      color: isActive ? config.color : 'rgba(255,255,255,0.6)',
                    }}
                  >
                    <IconComponent className={`${isActive ? 'w-7 h-7' : 'w-6 h-6'} transition-all`} />
                  </div>

                  {/* Name (Only for active mode, below icon) */}
                  {isActive && (
                    <span
                      className="text-xs font-semibold tracking-wide text-center leading-tight"
                      style={{ color: config.color }}
                    >
                      {config.name}
                    </span>
                  )}
                </button>
              );
            });
          })()}
        </div>

        {/* Description Section (Below selectors) */}
        <div className="px-4 py-3 text-center">
          <p className="text-sm text-white/60 leading-relaxed">
            {MODE_CONFIGS[currentModeId as AudioMode].description}
          </p>
        </div>

        {/* Timer Controls - Mobile only (below description) */}
        <div className="px-4 py-4 border-t border-white/5">
          <TimerControls
            currentModeId={currentModeId as AudioMode}
            currentTimerProfile={currentTimerProfile}
            onSelectTimerProfile={onSelectTimerProfile}
          />
        </div>
      </div>

      {/* ================================================================ */}
      {/* DESKTOP: Vertical List with Full Cards */}
      {/* ================================================================ */}
      <div className="hidden md:flex md:flex-col md:flex-1 overflow-y-auto scrollbar-hide p-4 gap-3 min-h-0">
        {modes.map((modeId) => {
          const config = MODE_CONFIGS[modeId];
          const isActive = currentModeId === modeId;
          const IconComponent = MODE_ICONS[modeId];

          return (
            <button
              key={modeId}
              onClick={() => onSelectMode(modeId)}
              className={`
                w-full p-4 rounded-lg transition-all duration-300
                flex items-start gap-4 text-left group
                ${
                  isActive
                    ? 'bg-white/10 border-2 shadow-lg'
                    : 'bg-white/5 border-2 border-transparent hover:bg-white/8 hover:border-white/10'
                }
              `}
              style={{
                borderColor: isActive ? config.color : 'transparent',
                boxShadow: isActive
                  ? `0 0 20px ${config.color}40`
                  : 'none',
              }}
              aria-label={`Seleccionar modo ${config.name}`}
              aria-pressed={isActive}
            >
              {/* Icon */}
              <div
                className={`
                  p-2 rounded-lg transition-colors
                  ${isActive ? 'bg-white/15' : 'bg-white/5 group-hover:bg-white/10'}
                `}
                style={{
                  backgroundColor: isActive ? `${config.color}20` : undefined,
                  color: isActive ? config.color : 'rgba(255,255,255,0.6)',
                }}
              >
                <IconComponent className="w-6 h-6 transition-colors" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3
                  className={`
                    font-semibold text-base mb-1 transition-colors
                    ${isActive ? 'text-white' : 'text-white/80 group-hover:text-white'}
                  `}
                >
                  {config.name}
                </h3>
                <p
                  className={`
                    text-xs text-white/50 leading-relaxed transition-all duration-300
                    ${isActive ? '' : 'line-clamp-2'}
                  `}
                >
                  {config.description}
                </p>
              </div>

              {/* Active Indicator */}
              {isActive && (
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0 mt-2 animate-pulse"
                  style={{ backgroundColor: config.color }}
                  aria-label="Actualmente activo"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Timer Profile Section (Bottom) - Desktop only */}
      <div className="hidden md:block mt-auto">
        <div className="p-4 pb-8 border-t border-white/5 bg-neutral-900/80">
          <TimerControls
            currentModeId={currentModeId as AudioMode}
            currentTimerProfile={currentTimerProfile}
            onSelectTimerProfile={onSelectTimerProfile}
          />
        </div>

        {/* Footer Link: Science */}
        <div className="px-4 pb-4 border-t border-white/5 pt-4 bg-neutral-900/80">
          <Link
            to="/science"
            className="flex items-center justify-center gap-2 text-xs text-white/40 hover:text-white/60 transition-colors"
          >
            <FlaskConical className="w-3.5 h-3.5" />
            <span>Science</span>
          </Link>
        </div>
      </div>
    </aside>
  );
};

export default PlayerSidebar;